import { HttpService } from '@utils/services/http.service';
import { UrlService } from '@utils/services/url-service.service';
import { inject, Injectable, signal } from '@angular/core';
import { EditRate, Rate } from '@interfaces/financial-indicators/rate.interface';
import { ApiModuleEnum } from '@utils/interfaces/api-module.constant';
import { ServerListResponse, ServerPostResponse } from '@utils/interfaces/serverResponses';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RatesService {

  private readonly _http = inject(HttpService);
  private readonly _urlService = inject(UrlService);
  private readonly URL = this._urlService.getEndpointUrl(
    { module: ApiModuleEnum.FINANCIAL_INDICATORS, version: 'v1', path: 'rate' });
  private _rateData = signal<Rate | null>(null);
  public rateData = this._rateData.asReadonly();

  public getRateData() {
    return this._http.GET<ServerListResponse<Rate[]>>({ url: this.URL }).pipe(
      map(({ data }) => {
        const highestDate = data.reduce((prev, current) => {
          if (current.year > prev.year) {
            return current;
          } else if (current.year === prev.year) {
            return current.month > prev.month ? current : prev;
          } else {
            return prev;
          }
        });
        this._rateData.set(highestDate);
      }),
      catchError(() => of({ data: [] }))
    );
  }

  public getCurrentMonthRate(): Observable<Rate | null> {
    const url = this._urlService.getEndpointUrl({
      module: ApiModuleEnum.FINANCIAL_INDICATORS,
      version: 'v1',
      path: 'current_month'
    });
    return this._http.GET<Rate>({ url }).pipe(
      tap((data) => {
        this._rateData.set(data);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          this._rateData.set(null);
          return of(null);
        }
        return throwError(() => error);
      })
    );
  }

  public createRate(body: FormData) {
    return this._http.POST<ServerPostResponse>({ url: this.URL, body });
  }

  public getRateSpecificData(month: number, year: number) {
    const url = `${this.URL}?month=${month}&year=${year}`;
    return this._http.GET<ServerListResponse<Rate[]>>({ url });
  }

  public fetchRateData(page: number, size: number, params = {}) {
    return this._http.listPagination<ServerListResponse<Rate[]>>(page, size, params, this.URL);
  }

  public updateRate(id: number, body: EditRate): Observable<void> {
    return this._http.PATCH<void>({ url: `${this.URL}/${id}`, body });
  }
}
